import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider, getToken as getAppCheckToken } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { initializeFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const hasRequiredConfig =
  firebaseConfig.apiKey &&
  firebaseConfig.projectId &&
  firebaseConfig.authDomain;

const app = hasRequiredConfig ? initializeApp(firebaseConfig) : null;

/**
 * App Check (reCAPTCHA v3). Required by the unauthenticated password-reset
 * endpoint; harmless elsewhere until enforcement is turned on per service.
 * Local dev: set REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN (see .env.example).
 */
let careAppCheck = null;
if (app && typeof window !== 'undefined') {
  const debugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
  if (debugToken) {
    const useAuto = debugToken === 'true' || debugToken === '1' || debugToken === 'yes';
    window.self.FIREBASE_APPCHECK_DEBUG_TOKEN = useAuto ? true : debugToken;
  }
  const siteKey = process.env.REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY;
  if (siteKey) {
    try {
      careAppCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      console.warn('Firebase App Check init failed:', e.message);
    }
  } else if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    console.warn('[Trilogy Care] REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY is not set; password reset requests will be rejected.');
  }
}
export { careAppCheck };

/** Headers to attach to fetch() calls that require App Check. Empty when unavailable. */
export async function appCheckHeaders() {
  if (!careAppCheck) return {};
  try {
    const { token } = await getAppCheckToken(careAppCheck, false);
    return token ? { 'X-Firebase-AppCheck': token } : {};
  } catch (e) {
    console.warn('App Check getToken failed:', e?.message || e);
    return {};
  }
}

export const auth = app ? getAuth(app) : null;
/**
 * Auto-detect long polling: care-team desktops on corporate networks showed
 * repeated QUIC/WebChannel Listen transport errors (QUIC_TOO_MANY_RTOS,
 * QUIC_PUBLIC_RESET), forcing every dashboard listener to re-sync on each
 * blip. Auto-detection lets those clients fall back to long polling.
 */
export const db = app
  ? initializeFirestore(app, { experimentalAutoDetectLongPolling: true })
  : null;
export const storage = app ? getStorage(app) : null;
export const functions = app ? getFunctions(app, 'us-central1') : null;
export const functionsBaseUrl =
  app && firebaseConfig.projectId
    ? `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net`
    : null;
export { httpsCallable };

let messaging = null;
try {
  if (app && typeof window !== 'undefined' && 'Notification' in window) {
    messaging = getMessaging(app);
  }
} catch (e) {
  console.warn('Firebase Messaging not available:', e.message);
}
export { messaging };

export async function requestFCMToken() {
  if (!messaging) return null;
  try {
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });
    return token;
  } catch (e) {
    console.warn('FCM token request failed:', e.message);
    return null;
  }
}

export function onForegroundMessage(callback) {
  if (!messaging) return () => {};
  return onMessage(messaging, callback);
}
